import type { PropsWithChildren } from 'react';
import { RectanglePortraitStackIcon } from '@zep-us/zus-icons';
import Link from 'next/link';
import { AuthorTag } from '@/components/AuthorTag';
import { useFormatLargeNumber } from '@/hooks/number/useLargeNumber';
import { PlayIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';
import { FOLDER_COPY } from '../consts/copy';
import type { FolderSummary } from '../types/folder.types';
import { FolderCardMenu } from './FolderCardMenu';
import { FolderCategoryChips } from './FolderCategoryChips';
import { FolderThumbnail } from './FolderThumbnail';
interface FolderCardProps {
  folder: FolderSummary;
  /**
   * 크롤러가 따라갈 수 있어야 하는 목록(자료실)은 이 값을 준다. 내 폴더처럼 로그인 뒤에만 보이는
   * 목록은 `onClick` 으로 충분하다.
   */
  href?: string;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** 남의 폴더가 섞이는 목록에서만 켠다. 내 폴더 목록에서는 내 이름만 반복돼 소음이 된다. */
  showAuthor?: boolean;
}
export const FolderCard = ({
  folder,
  href,
  onClick,
  onEdit,
  onDelete,
  showAuthor = false
}: FolderCardProps) => {
  return <CardFrame href={href} onClick={onClick} data-sentry-element="CardFrame" data-sentry-component="FolderCard" data-sentry-source-file="FolderCard.tsx">
      <FolderThumbnail imageUrls={folder.thumbnailUrls} data-sentry-element="FolderThumbnail" data-sentry-source-file="FolderCard.tsx" />
      <div className={cn('flex min-h-[157px] w-full min-w-px flex-1 flex-col justify-center gap-xs', 'border-b border-alternative py-lg', 'max-tablet-lg:min-h-[116px] max-tablet-lg:gap-sm max-tablet-lg:py-md')}>
        <div className={'flex flex-col gap-2xs pr-[20px] max-tablet-lg:gap-3xs max-tablet-lg:pr-none'}>
          <div className={'flex w-full justify-between gap-2xs max-tablet-sm:h-[32px] max-tablet-sm:items-center max-tablet-sm:gap-3xs'}>
            <div className={'flex min-w-0 flex-1 items-center overflow-hidden'}>
              <FolderCategoryChips chips={folder.chips} data-sentry-element="FolderCategoryChips" data-sentry-source-file="FolderCard.tsx" />
            </div>
            {(onEdit || onDelete) && <FolderCardMenu onEdit={onEdit} onDelete={onDelete} />}
          </div>
          <p className="word-break-all line-clamp-2 max-w-full text-body-lg-bold text-default tablet-sm:text-heading-sm-bold">
            {folder.name}
          </p>
        </div>
        {/*
          작성자와 메타 묶음. **`QuizListItem` 의 `CardContentBottom` 과 값이 같아야 한다** —
          자료실은 두 종류를 한 그리드에 섞으므로 여기가 어긋나면 행 높이가 갈린다.
          시안도 두 카드의 `Bottom` 을 똑같이 45px(20 + gap 6 + 19)로 잡는다.
           퀴즈 쪽은 `isPortable` 로 분기하지만 여기서는 CSS 로 같은 결과를 낸다 —
          `isPortable = isMobile || isTablet` 이 곧 `max-tablet-lg`(1024px) 다.
          `laptop-sm:min-h-[40px]` 도 그대로 가져온다. 작성자를 숨긴 목록(내 퀴즈)에서
          이 값이 없으면 퀴즈 행만 40px 로 남아 두 섹션의 행 높이가 달라진다.
         */}
        <div className={cn('flex w-full flex-col gap-2xs laptop-sm:min-h-[40px]', 'max-tablet-lg:flex-row max-tablet-lg:items-center max-tablet-lg:gap-none')}>
          {showAuthor && <AuthorTag authorName={folder.ownerName} authorHashId={folder.ownerId} className={'max-mobile-lg:text-caption-lg-medium'} />}
          <FolderMetaInfo folder={folder} data-sentry-element="FolderMetaInfo" data-sentry-source-file="FolderCard.tsx" />
        </div>
      </div>
    </CardFrame>;
};

/**
 * 행 껍데기. `href` 가 있으면 앵커로, 없으면 클릭 가능한 div 로 그린다.
 * 지오메트리는 `QuizListItem` 의 행과 같은 값이라 자료실에서 두 종류가 섞여도 정렬선이 맞는다.
 */
const CardFrame = ({
  href,
  onClick,
  children
}: PropsWithChildren<{
  href?: string;
  onClick?: () => void;
}>) => {
  const className = cn('group relative flex w-full items-center gap-md px-3xs py-none', 'max-tablet-lg:gap-sm', (href || onClick) && 'cursor-pointer hover:bg-neutral');
  if (href) {
    return <Link prefetch={false} href={href} className={className} onClick={onClick}>
        {children}
      </Link>;
  }
  return <div className={className} onClick={onClick} data-sentry-component="CardFrame" data-sentry-source-file="FolderCard.tsx">
      {children}
    </div>;
};
const FolderMetaInfo = ({
  folder
}: {
  folder: FolderSummary;
}) => {
  const formatLargeNumber = useFormatLargeNumber();
  return <div className={cn('flex w-full items-center gap-md', 'max-tablet-lg:justify-end max-tablet-lg:gap-xs')} data-sentry-component="FolderMetaInfo" data-sentry-source-file="FolderCard.tsx">
      <span className={'flex shrink-0 items-center gap-3xs text-body-sm-regular text-alternative'}>
        <RectanglePortraitStackIcon size={'2xs'} data-sentry-element="RectanglePortraitStackIcon" data-sentry-source-file="FolderCard.tsx" />
        {FOLDER_COPY.quizCount(formatLargeNumber(folder.quizsetCount))}
      </span>
      <span className={'flex shrink-0 items-center gap-3xs text-body-sm-regular text-alternative'}>
        <PlayIcon size={'2xs'} data-sentry-element="PlayIcon" data-sentry-source-file="FolderCard.tsx" />
        {formatLargeNumber(folder.allPlayCount)}
      </span>
    </div>;
};